import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, apiError, fmtIDR } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { GameConfigCard } from "@/components/GameConfigCard";
import { SalesReport } from "@/components/SalesReport";
import { CancellationReport } from "@/components/CancellationReport";
import { NotificationBroadcast } from "@/components/NotificationBroadcast";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TrendingUp, Users, Store, Building2, Trash2, Plus, ShoppingBag, UserCog, Pencil, Ticket, Upload, ImageIcon, Star, Search, Gamepad2, ArrowLeft } from "lucide-react";
import { AttendancePanel } from "@/components/AttendancePanel";
import { SupplierPanel } from "@/components/SupplierPanel";
import { RecipePanel } from "@/components/RecipePanel";
import { CatalogPanel } from "@/components/CatalogPanel";
import { SpecialDiscountPanel } from "@/components/SpecialDiscountPanel";
import { OwnerAccountsPanel } from "@/components/OwnerAccountsPanel";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TABS = [["overview", "Ringkasan"], ["catalog", "Katalog & Stok"], ["special", "Diskon Khusus"], ["staff", "Staf"], ["owners", "Akun Owner"], ["members", "Member"], ["branches", "Cabang"], ["attendance", "Absensi"], ["suppliers", "Supplier"], ["recipes", "Resep & SOP"], ["cfd", "Foto CFD"], ["cancellations", "Pembatalan"], ["notif", "Notifikasi"], ["game", "Game & Voucher"]];

export default function OwnerDashboard({ onHome }) {
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [staff, setStaff] = useState([]);
  const [members, setMembers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showStaff, setShowStaff] = useState(false);
  const [showBranch, setShowBranch] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [voucherMember, setVoucherMember] = useState(null);
  const [memberVouchers, setMemberVouchers] = useState([]);
  const openMemberVouchers = async (m) => {
    setVoucherMember(m);
    try { const { data } = await api.get(`/members/${m.id}/vouchers`); setMemberVouchers(data); }
    catch (e) { setMemberVouchers([]); }
  };
  const [nStaff, setNStaff] = useState({ name: "", username: "", email: "", password: "", role: "kasir", branch_id: "", branch_ids: [] });
  const [nBranch, setNBranch] = useState({ name: "", address: "" });
  const [memberSearch, setMemberSearch] = useState("");
  const [opsBranchId, setOpsBranchId] = useState("");
  const opsBranch = branches.find((b) => b.id === opsBranchId) || branches[0];

  const load = async () => {
    try {
      const [s, st, mb, br] = await Promise.all([api.get("/reports/summary"), api.get("/staff"), api.get("/members"), api.get("/branches")]);
      setSummary(s.data); setStaff(st.data); setMembers(mb.data); setBranches(br.data);
      if (!opsBranchId && br.data[0]) setOpsBranchId(br.data[0].id);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const addStaff = async () => {
    if (nStaff.role === "keuangan") { if (!nStaff.branch_ids || nStaff.branch_ids.length === 0) { toast.error("Pilih minimal satu cabang untuk staf Keuangan"); return; } }
    else if (!nStaff.branch_id) { toast.error("Pilih cabang untuk staf ini"); return; }
    try { await api.post("/staff", nStaff); toast.success("Staf ditambahkan"); setShowStaff(false); setNStaff({ name: "", username: "", email: "", password: "", role: "kasir", branch_id: "", branch_ids: [] }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delStaff = async (id) => { try { await api.delete(`/staff/${id}`); toast.success("Staf dihapus"); load(); } catch (e) { toast.error(apiError(e)); } };
  const addBranch = async () => { try { await api.post("/branches", nBranch); toast.success("Cabang ditambahkan"); setShowBranch(false); load(); } catch (e) { toast.error(apiError(e)); } };
  const delBranch = async (id) => { if (!window.confirm("Hapus cabang ini? Tindakan tidak bisa dibatalkan.")) return; try { await api.delete(`/branches/${id}`); toast.success("Cabang dihapus"); load(); } catch (e) { toast.error(apiError(e)); } };
  const saveMember = async () => {
    try { await api.put(`/members/${editMember.id}`, { name: editMember.name, email: editMember.email, username: editMember.username, phone: editMember.phone, address: editMember.address }); toast.success("Member diperbarui"); setEditMember(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delMember = async (id) => { try { await api.delete(`/members/${id}`); toast.success("Member dihapus"); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="min-h-screen bg-hotpot-grid pb-10">
      <TopBar />
      <div className="mx-auto max-w-[1400px] px-4 pt-6 sm:px-6">
        {onHome && <button data-testid="owner-home-btn" onClick={onHome} className="mb-3 inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-crimson"><ArrowLeft size={16} /> Menu Utama</button>}
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-neutral-900 sm:text-3xl">Command Center</h1>
        <p className="mb-5 text-sm text-muted-foreground">Akses penuh seluruh operasional DA LE TANG HuoGuo.</p>

        <div className="mb-6 flex flex-wrap gap-2">
          {TABS.map(([k, l]) => (
            <button key={k} data-testid={`owner-tab-${k}`} onClick={() => setTab(k)}
              className={`touch-btn rounded-xl px-5 text-sm font-bold transition-all ${tab === k ? "bg-crimson text-white" : "card-surface text-muted-foreground hover:text-white"}`}>{l}</button>
          ))}
        </div>

        {tab === "overview" && summary && (
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Metric icon={TrendingUp} label="Total Pendapatan" value={fmtIDR(summary.total_revenue)} glow="gold" testid="metric-revenue" />
              <Metric icon={ShoppingBag} label="Transaksi" value={summary.transaction_count} testid="metric-txn" />
              <Metric icon={Users} label="Member" value={summary.members} testid="metric-members" />
              <Metric icon={Building2} label="Staf" value={summary.staff} testid="metric-staff" />
            </div>
            <Card className="card-surface rounded-2xl p-5">
              <div className="mb-3 font-display font-bold text-neutral-900">Pendapatan per Cabang</div>
              {summary.by_branch.length === 0 ? <p className="text-sm text-muted-foreground">Belum ada transaksi.</p> :
                summary.by_branch.map((b) => (
                  <div key={b.name} className="mb-2">
                    <div className="mb-1 flex justify-between text-sm"><span className="text-neutral-900">{b.name}</span><span className="font-mono-num text-gold">{fmtIDR(b.revenue)}</span></div>
                    <div className="h-2 rounded-full bg-neutral-100"><div className="h-2 rounded-full bg-crimson" style={{ width: `${Math.min(100, (b.revenue / (summary.total_revenue || 1)) * 100)}%` }} /></div>
                  </div>
                ))}
            </Card>
            <SalesReport owner branches={branches} />
          </div>
        )}

        {tab === "staff" && (
          <Card className="card-surface rounded-2xl p-5">
            <div className="mb-4 flex items-center justify-between">
              <div className="font-display text-lg font-bold text-neutral-900">Manajemen Staf</div>
              <Button data-testid="add-staff-btn" onClick={() => { setNStaff({ name: "", username: "", email: "", password: "", role: "kasir", branch_id: "", branch_ids: [] }); setShowStaff(true); }} className="bg-crimson font-bold hover:bg-red-500"><Plus size={16} className="mr-1" /> Tambah Staf</Button>
            </div>
            <div className="space-y-2">
              {staff.map((s) => (
                <div key={s.id} data-testid={`staff-row-${s.username}`} className="flex items-center justify-between rounded-xl bg-neutral-100 px-4 py-3">
                  <div>
                    <div className="font-semibold text-neutral-900">{s.name} <span className="ml-2 rounded-full bg-crimson/20 px-2 py-0.5 text-[10px] font-bold uppercase text-crimson">{s.role}</span> <span className="ml-1 rounded-full bg-gold/15 px-2 py-0.5 text-[10px] font-bold text-gold">{s.branch_name}</span></div>
                    <div className="text-xs text-muted-foreground">@{s.username} · {s.email}</div>
                  </div>
                  <Button data-testid={`del-staff-${s.username}`} onClick={() => delStaff(s.id)} variant="ghost" size="icon" className="text-muted-foreground hover:text-crimson"><Trash2 size={16} /></Button>
                </div>
              ))}
              {staff.length === 0 && <p className="text-sm text-muted-foreground">Belum ada staf.</p>}
            </div>
          </Card>
        )}

        {tab === "members" && (
          <Card className="card-surface rounded-2xl p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 font-display text-lg font-bold text-neutral-900"><UserCog className="text-crimson" size={18} /> Daftar Member ({members.length})</div>
              <div className="relative w-full sm:w-80">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="member-search" value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} placeholder="Cari No HP / email / username / nama" className="bg-neutral-100 border-neutral-200 pl-9" />
              </div>
            </div>
            <div className="space-y-2" data-testid="members-list">
              {members.filter((m) => { const q = memberSearch.trim().toLowerCase(); return !q || [m.name, m.phone, m.email, m.username].some((v) => (v || "").toLowerCase().includes(q)); }).map((m) => (
                <div key={m.id} data-testid={`member-row-${m.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-neutral-100 px-4 py-3">
                  <div className="min-w-[180px]">
                    <div className="font-semibold text-neutral-900">{m.name}</div>
                    <div className="text-xs text-muted-foreground">@{m.username} · {m.email}{m.phone ? ` · ${m.phone}` : ""}</div>
                    {m.address ? <div className="text-[11px] text-muted-foreground/80">📍 {m.address}</div> : null}
                  </div>
                  <div className="flex items-center gap-2">
                    <span data-testid={`member-score-${m.id}`} className="inline-flex items-center gap-1 rounded-full bg-crimson/10 px-2.5 py-1 text-[11px] font-bold text-crimson"><Gamepad2 size={12} /> {Number(m.game_score || 0).toLocaleString("id-ID")}</span>
                    <span data-testid={`member-points-${m.id}`} className="inline-flex items-center gap-1 rounded-full bg-yellow-400/15 px-2.5 py-1 text-[11px] font-bold text-yellow-600"><Star size={12} className="fill-yellow-400 text-yellow-400" /> {Number(m.points || 0).toLocaleString("id-ID")} poin</span>
                    <span className="rounded-full bg-gold/15 px-2 py-1 text-[10px] font-bold text-gold">{m.voucher_count} voucher</span>
                    <Button data-testid={`view-vouchers-${m.id}`} onClick={() => openMemberVouchers(m)} variant="ghost" size="icon" className="text-emerald-400 hover:bg-emerald-400/10"><Ticket size={16} /></Button>
                    <Button data-testid={`edit-member-${m.id}`} onClick={() => setEditMember({ ...m })} variant="ghost" size="icon" className="text-gold hover:bg-gold/10"><Pencil size={16} /></Button>
                    <Button data-testid={`del-member-${m.id}`} onClick={() => delMember(m.id)} variant="ghost" size="icon" className="text-muted-foreground hover:text-crimson"><Trash2 size={16} /></Button>
                  </div>
                </div>
              ))}
              {members.length === 0 && <p className="text-sm text-muted-foreground">Belum ada member terdaftar.</p>}
            </div>
          </Card>
        )}

        {tab === "branches" && (
          <Card className="card-surface rounded-2xl p-5">
            <div className="mb-4 flex items-center justify-between">
              <div className="font-display text-lg font-bold text-neutral-900">Cabang</div>
              <Button data-testid="add-branch-btn" onClick={() => setShowBranch(true)} className="bg-crimson font-bold hover:bg-red-500"><Plus size={16} className="mr-1" /> Tambah Cabang</Button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {branches.map((b) => (
                <div key={b.id} data-testid={`branch-card-${b.id}`} className="rounded-xl bg-neutral-100 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 font-semibold text-neutral-900"><Store size={16} className="text-gold" /> {b.name}</div>
                    <Button data-testid={`del-branch-${b.id}`} onClick={() => delBranch(b.id)} variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-crimson"><Trash2 size={15} /></Button>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">{b.address}</div>
                  <div className="mt-2 flex gap-4 text-xs"><span className="text-neutral-900">PB1: <b className="text-gold">{b.tax_pb1}%</b></span><span className="text-neutral-900">Service: <b className="text-gold">{b.service_charge}%</b></span></div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {(tab === "catalog" || tab === "special") && (
          <div className="mb-4 flex items-center gap-2" data-testid="owner-ops-branch-bar">
            <span className="text-sm font-semibold text-neutral-700">Cabang:</span>
            <Select value={opsBranch?.id || ""} onValueChange={setOpsBranchId}>
              <SelectTrigger data-testid="owner-ops-branch" className="w-64 bg-neutral-100 border-neutral-200"><SelectValue placeholder="Pilih cabang" /></SelectTrigger>
              <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {tab === "catalog" && <CatalogPanel branch={opsBranch} />}
        {tab === "special" && <SpecialDiscountPanel branch={opsBranch} />}

        {tab === "owners" && <OwnerAccountsPanel />}
        {tab === "attendance" && <AttendancePanel owner branches={branches} />}
        {tab === "suppliers" && <SupplierPanel />}
        {tab === "recipes" && <RecipePanel />}
        {tab === "cfd" && <CfdPhotos />}
        {tab === "cancellations" && <CancellationReport owner branches={branches} />}
        {tab === "notif" && <NotificationBroadcast />}
        {tab === "game" && <GameConfigCard owner />}
      </div>

      <Dialog open={showStaff} onOpenChange={setShowStaff}>
        <DialogContent className="glass border-neutral-200 text-neutral-900">
          <DialogHeader><DialogTitle className="font-display">Tambah Staf</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-muted-foreground">Nama</Label><Input data-testid="staff-name" placeholder="Nama lengkap" value={nStaff.name} onChange={(e) => setNStaff({ ...nStaff, name: e.target.value })} className="mt-1 bg-neutral-100 border-neutral-200" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs text-muted-foreground">Username</Label><Input data-testid="staff-username" placeholder="username" value={nStaff.username} onChange={(e) => setNStaff({ ...nStaff, username: e.target.value })} className="mt-1 bg-neutral-100 border-neutral-200" /></div>
              <div><Label className="text-xs text-muted-foreground">Password</Label><Input data-testid="staff-password" type="password" placeholder="••••••" value={nStaff.password} onChange={(e) => setNStaff({ ...nStaff, password: e.target.value })} className="mt-1 bg-neutral-100 border-neutral-200" /></div>
            </div>
            <div><Label className="text-xs text-muted-foreground">Email</Label><Input data-testid="staff-email" placeholder="email@domain.com" value={nStaff.email} onChange={(e) => setNStaff({ ...nStaff, email: e.target.value })} className="mt-1 bg-neutral-100 border-neutral-200" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-muted-foreground">Peran</Label>
                <Select value={nStaff.role} onValueChange={(v) => setNStaff({ ...nStaff, role: v })}>
                  <SelectTrigger data-testid="staff-role" className="mt-1 bg-neutral-100 border-neutral-200"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="manager">Manager Cabang</SelectItem><SelectItem value="kasir">Kasir</SelectItem><SelectItem value="dapur">Admin Dapur</SelectItem><SelectItem value="keuangan">Staf Keuangan</SelectItem></SelectContent>
                </Select>
              </div>
              {nStaff.role !== "keuangan" && (
              <div>
                <Label className="text-xs text-muted-foreground">Cabang</Label>
                <Select value={nStaff.branch_id} onValueChange={(v) => setNStaff({ ...nStaff, branch_id: v })}>
                  <SelectTrigger data-testid="staff-branch" className="mt-1 bg-neutral-100 border-neutral-200"><SelectValue placeholder="Pilih cabang" /></SelectTrigger>
                  <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              )}
            </div>
            {nStaff.role === "keuangan" && (
              <div data-testid="staff-finance-branches">
                <Label className="text-xs text-muted-foreground">Cabang yang Dikelola (Keuangan)</Label>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  {branches.map((b) => {
                    const checked = nStaff.branch_ids?.includes(b.id);
                    return (
                      <label key={b.id} data-testid={`staff-fin-branch-${b.id}`} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${checked ? "border-crimson bg-crimson/10 font-semibold text-crimson" : "border-neutral-200 bg-neutral-100 text-neutral-700"}`}>
                        <input type="checkbox" checked={!!checked} onChange={(e) => { const set = new Set(nStaff.branch_ids || []); e.target.checked ? set.add(b.id) : set.delete(b.id); setNStaff({ ...nStaff, branch_ids: Array.from(set) }); }} />
                        {b.name}
                      </label>
                    );
                  })}
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">Staf Keuangan hanya bisa mengelola keuangan cabang yang dicentang. Bisa pilih lebih dari satu (atau semua) cabang.</p>
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">Staf hanya dapat mengelola data cabang yang dipilih.</p>
            <Button data-testid="save-staff-btn" onClick={addStaff} className="w-full bg-crimson font-bold hover:bg-red-500">Simpan Staf</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showBranch} onOpenChange={setShowBranch}>
        <DialogContent className="glass border-neutral-200 text-neutral-900">
          <DialogHeader><DialogTitle className="font-display">Tambah Cabang</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="branch-name" placeholder="Nama Cabang" value={nBranch.name} onChange={(e) => setNBranch({ ...nBranch, name: e.target.value })} className="bg-neutral-100 border-neutral-200" />
            <Input data-testid="branch-address" placeholder="Alamat" value={nBranch.address} onChange={(e) => setNBranch({ ...nBranch, address: e.target.value })} className="bg-neutral-100 border-neutral-200" />
            <p className="text-[11px] text-muted-foreground">PB1 diatur oleh Manager Cabang, dan Service Charge oleh Kasir (dengan otorisasi Manager). Tidak perlu diisi saat menambah cabang.</p>
            <Button data-testid="save-branch-btn" onClick={addBranch} className="w-full bg-crimson font-bold hover:bg-red-500">Simpan</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editMember} onOpenChange={(o) => !o && setEditMember(null)}>
        <DialogContent className="glass border-neutral-200 text-neutral-900">
          <DialogHeader><DialogTitle className="font-display">Edit Member</DialogTitle></DialogHeader>
          {editMember && (
            <div className="space-y-3">
              <Input data-testid="member-name" placeholder="Nama" value={editMember.name || ""} onChange={(e) => setEditMember({ ...editMember, name: e.target.value })} className="bg-neutral-100 border-neutral-200" />
              <Input data-testid="member-username" placeholder="Username" value={editMember.username || ""} onChange={(e) => setEditMember({ ...editMember, username: e.target.value })} className="bg-neutral-100 border-neutral-200" />
              <Input data-testid="member-email" placeholder="Email" value={editMember.email || ""} onChange={(e) => setEditMember({ ...editMember, email: e.target.value })} className="bg-neutral-100 border-neutral-200" />
              <Input data-testid="member-phone" placeholder="No HP" value={editMember.phone || ""} onChange={(e) => setEditMember({ ...editMember, phone: e.target.value })} className="bg-neutral-100 border-neutral-200" />
              <Input data-testid="member-address" placeholder="Alamat" value={editMember.address || ""} onChange={(e) => setEditMember({ ...editMember, address: e.target.value })} className="bg-neutral-100 border-neutral-200" />
              <Button data-testid="save-member-btn" onClick={saveMember} className="w-full bg-crimson font-bold hover:bg-red-500">Simpan Perubahan</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!voucherMember} onOpenChange={(o) => !o && setVoucherMember(null)}>
        <DialogContent className="glass border-neutral-200 text-neutral-900 max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Ticket size={18} className="text-emerald-400" /> Voucher {voucherMember?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2" data-testid="member-vouchers-list">
            {memberVouchers.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Member ini belum punya voucher.</p>}
            {memberVouchers.map((v) => {
              const point = v.source === "point";
              const gift = v.source === "gift";
              const exp = v.expires_at ? new Date(v.expires_at) : null;
              const expired = exp ? Date.now() > exp.getTime() : false;
              const daysLeft = exp ? Math.ceil((exp.getTime() - Date.now()) / 86400000) : null;
              const badgeBg = point ? "bg-yellow-400 text-black" : gift ? "bg-emerald-500 text-white" : "bg-crimson text-white";
              const badgeLabel = point ? "⭐ Poin" : gift ? "🎁 Hadiah" : v.type === "nominal" ? "Diskon Rp" : v.type === "percent" ? "Diskon %" : "Free Item";
              return (
                <div key={v.id} data-testid={`owner-voucher-${v.code}`} className={`rounded-xl bg-neutral-100 p-3 ${(v.status === "used" || expired) ? "opacity-60" : ""}`}>
                  <div className="flex items-center justify-between">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${badgeBg}`}>{badgeLabel}</span>
                    <span className="font-mono-num text-sm font-bold text-gold">{v.type === "nominal" ? `Rp ${Number(v.value).toLocaleString("id-ID")}` : v.type === "percent" ? `${v.value}%` : v.free_item}</span>
                  </div>
                  {v.label ? <div className="mt-0.5 text-[11px] text-muted-foreground">{v.label}</div> : null}
                  <div className="mt-1 font-mono-num text-xs text-neutral-900">{v.code}</div>
                  <div className={`text-[11px] ${expired ? "text-crimson" : v.status === "used" ? "text-muted-foreground" : "text-emerald-400"}`}>
                    {v.status === "used" ? "Sudah diredeem" : expired ? "Kedaluwarsa" : "Aktif"}{v.status !== "used" && !expired ? (exp ? ` · s/d ${exp.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" })}${daysLeft != null ? ` (${daysLeft} hari lagi)` : ""}` : " · Tanpa batas waktu") : ""}
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CfdPhotos() {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [bust, setBust] = useState(Date.now());
  const loadPhotos = async () => { try { const { data } = await api.get("/cfd/photos"); setPhotos(data); } catch (e) {} };
  useEffect(() => { loadPhotos(); }, []);
  const upload = async (fl) => {
    const f = fl?.[0]; if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("File harus gambar"); return; }
    if (f.size > 6 * 1024 * 1024) { toast.error("Ukuran gambar maksimal 6MB"); return; }
    if (photos.length >= 6) { toast.error("Maksimal 6 foto"); return; }
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", f); await api.post("/cfd/photos", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Foto CFD ditambahkan · berlaku untuk semua cabang"); setBust(Date.now()); loadPhotos(); }
    catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };
  const remove = async (pid) => { try { await api.delete(`/cfd/photos/${pid}`); toast.success("Foto dihapus"); loadPhotos(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <Card className="card-surface rounded-2xl p-5" data-testid="cfd-photos-tab">
      <div className="mb-1 flex items-center gap-2 font-display text-lg font-bold text-neutral-900"><ImageIcon className="text-crimson" size={18} /> Foto Selamat Datang CFD (Semua Cabang)</div>
      <p className="mb-4 text-xs text-muted-foreground">Foto tampil di Layar Pelanggan semua cabang saat tidak ada pesanan. Bisa sampai <b className="text-gold">6 foto</b> — bergantian otomatis tiap <b className="text-gold">5 detik</b> dengan transisi halus. Rasio lebar (mis. 1600×900), maks 6MB per foto.</p>
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {photos.map((p) => (
          <div key={p.id} data-testid={`cfd-photo-${p.id}`} className="group relative overflow-hidden rounded-xl border border-neutral-200 bg-neutral-100">
            <img src={`${BACKEND}/api/cfd/photos/${p.id}?v=${bust}`} alt="" className="h-32 w-full object-cover" />
            <button data-testid={`cfd-photo-del-${p.id}`} onClick={() => remove(p.id)} className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-lg bg-black/60 text-white opacity-0 transition-opacity group-hover:opacity-100 hover:bg-crimson"><Trash2 size={15} /></button>
          </div>
        ))}
        {photos.length === 0 && <div className="col-span-full flex h-32 items-center justify-center rounded-xl border border-dashed border-neutral-300 text-sm text-muted-foreground">Belum ada foto — CFD memakai logo default</div>}
      </div>
      {photos.length < 6 ? (
        <label data-testid="cfd-upload-label" className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-crimson px-4 py-2.5 text-sm font-bold text-white hover:bg-red-500">
          <Upload size={16} /> {uploading ? "Mengunggah…" : `Tambah Foto (${photos.length}/6)`}
          <input data-testid="cfd-upload-input" type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => upload(e.target.files)} />
        </label>
      ) : (
        <p className="text-sm font-semibold text-muted-foreground">Sudah mencapai maksimal 6 foto. Hapus salah satu untuk menambah.</p>
      )}
    </Card>
  );
}

function Metric({ icon: Icon, label, value, glow, testid }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className={`card-surface rounded-2xl p-5 ${glow === "gold" ? "gold-glow" : ""}`}>
      <Icon className={glow === "gold" ? "text-gold" : "text-crimson"} size={22} />
      <div data-testid={testid} className="mt-3 font-mono-num text-2xl font-extrabold text-neutral-900">{value}</div>
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
    </motion.div>
  );
}
